import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Handshake, Percent, Wallet, Sparkles, ArrowRight } from "lucide-react";
import { API } from "@/config";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { ThemedPhoneInput } from "@/components/ui/ThemedPhoneInput";

const TYPES = [["hotel", "Hotel"], ["resort", "Resort"], ["vacation_rental", "Vacation rental company"], ["property_manager", "Property manager"], ["travel_agent", "Travel agent"], ["concierge", "Concierge"], ["influencer", "Influencer / blogger"], ["local_business", "Local business"], ["other", "Other"]];
const F = ({ label, children }) => <label className="block"><span className="eyebrow block mb-1.5">{label}</span>{children}</label>;
const inputCls = "w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2.5 rounded-lg focus:outline-none focus:border-[#FFD166]";

export default function PartnerApply() {
  const [f, setF] = useState({ business_name: "", contact_name: "", email: "", phone: "", paypal_email: "", website: "", notes: "", partner_type: "hotel" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/partners/apply`, f);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check the form and try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0F1E2C] text-[#F6F1E4]">
      <Helmet><title>Partner Program — Enchanted Gear Rentals</title></Helmet>
      <Nav />
      <main className="pt-32 pb-24">
        <div className="container grid lg:grid-cols-[1fr_520px] gap-12 items-start">
          <div>
            <div className="eyebrow mb-3">Affiliate &amp; Vendor Partner Program</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">Earn every time your guests rent with us.</h1>
            <p className="text-[#B9C8D5] text-base md:text-lg max-w-xl mb-8">Hotels, resorts, vacation-rental managers, travel agents, concierges and creators get one referral code. Your guests save on strollers, cribs and ECVs — and you earn a commission on every booking.</p>
            <div className="grid sm:grid-cols-3 gap-4 max-w-2xl">
              {[[Percent, "Guest discount", "Your code gives guests a discount on rental items."], [Wallet, "Commission", "Earn a percentage of eligible rental sales, paid via PayPal."], [Sparkles, "Dashboard", "Track code uses, earnings and payouts in real time."]].map(([Icon, t, d]) => (
                <div key={t} className="glass p-4"><Icon className="w-5 h-5 text-[#FFD166] mb-2" /><p className="font-medium">{t}</p><p className="text-sm text-[#B9C8D5] mt-1">{d}</p></div>
              ))}
            </div>
            <p className="text-sm text-[#B9C8D5] mt-8">Already a partner? <Link to="/partners/login" className="text-[#FFD166] hover:underline" data-testid="partner-login-link">Sign in to your dashboard <ArrowRight className="inline w-3.5 h-3.5" /></Link></p>
          </div>
          <div className="glass p-6 md:p-8" data-testid="partner-apply-card">
            {done ? (
              <div className="text-center py-8" data-testid="partner-apply-success">
                <Handshake className="w-10 h-10 text-[#FFD166] mx-auto mb-3" />
                <h2 className="font-serif text-2xl mb-2">Application received</h2>
                <p className="text-[#B9C8D5] text-sm">We review every partner personally. Once approved you'll get an email with your referral code and a link to set your dashboard password.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="partner-apply-form">
                <h2 className="font-serif text-2xl">Apply to become a partner</h2>
                <F label="Business / partner name"><input className={inputCls} value={f.business_name} onChange={set("business_name")} required data-testid="pa-business" /></F>
                <F label="Partner type"><select className={inputCls} value={f.partner_type} onChange={set("partner_type")} data-testid="pa-type">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></F>
                <F label="Contact person"><input className={inputCls} value={f.contact_name} onChange={set("contact_name")} required data-testid="pa-contact" /></F>
                <div className="grid sm:grid-cols-2 gap-4">
                  <F label="Email"><input type="email" className={inputCls} value={f.email} onChange={set("email")} required data-testid="pa-email" /></F>
                  <F label="Phone"><ThemedPhoneInput value={f.phone} onChange={(v) => setF((x) => ({ ...x, phone: v || "" }))} data-testid="pa-phone" /></F>
                </div>
                <F label="PayPal email (for payouts)"><input type="email" className={inputCls} value={f.paypal_email} onChange={set("paypal_email")} required data-testid="pa-paypal" /></F>
                <F label="Website / social (optional)"><input className={inputCls} value={f.website} onChange={set("website")} placeholder="https://" data-testid="pa-website" /></F>
                <F label="Notes (optional)"><textarea className={inputCls} rows={3} value={f.notes} onChange={set("notes")} data-testid="pa-notes" /></F>
                <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="pa-submit">{busy ? "Sending…" : "Submit application"}</button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
